'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router'

// walrus
import Box from '@components/kit/box'
import { BackIcon } from '@components/kit/icons'
import Pressable from '@components/kit/pressable'
import Text from '@components/kit/text'
import { useNavigate } from 'react-router-dom'

// lib
import AppShell from '@components/ui/app-shell'
import DataTable, { DataTableColumn } from '@components/ui/data-table'
import PageHeader from '@components/ui/page-header'
import { useGoalsStore } from '@store/Goals'
import { IGoalTask, IMemberGoal } from '@store/Goals/types'
import { useUserStore } from '@store/User'
import { IUserInfo } from '@store/User/types'

/* -----------------------------------------------------------------------------
 * columns
 *
 * Values render upper-cased, the way the previous inline table did.
 * -------------------------------------------------------------------------- */

const upperCell = (value?: string) => (
  <Text font='pro.text.13.regular' numberOfLines={1} textColor='proTextBody'>
    {value?.toUpperCase()}
  </Text>
)

const columns: DataTableColumn<IGoalTask>[] = [
  {
    key: 'status',
    label: 'Status',
    render: row => upperCell(row.status),
    width: 130
  },
  {
    key: 'type',
    label: 'Type',
    render: row => upperCell(row.type),
    width: 130
  },
  {
    flex: 1,
    key: 'title',
    label: 'Title',
    minWidth: 200,
    render: row => upperCell(row.title)
  },
  {
    flex: 2,
    key: 'description',
    label: 'Description',
    minWidth: 280,
    render: row => upperCell(row.description)
  }
]

/* -----------------------------------------------------------------------------
 * GoalActions
 * -------------------------------------------------------------------------- */

const GoalActions = () => {
  const navigate = useNavigate()
  const { userId } = useParams()
  const { getUserInfo } = useUserStore.getState()
  const [user, setUser] = useState<IUserInfo | null>(null)
  const visibleTasks = ['active', 'completed', 'postponed', 'ignored']
  const [allTasksList, setAllTasksList] = useState<IGoalTask[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const { getMemberGoals } = useGoalsStore.getState()
  const [memberGoals, setMemberGoals] = useState<IMemberGoal | null>(null)
  const [isMemberGoalsLoading, setIsMemberGoalsLoading] = useState(true)

  useEffect(() => {
    getMemberGoals(parseInt(userId)).then(goals => {
      setMemberGoals(goals)
      setIsMemberGoalsLoading(false)
    })
  }, [getMemberGoals, userId])

  const goBack = () => {
    navigate(`/chat/${userId}`)
  }

  useEffect(() => {
    getUserInfo(parseInt(userId)).then(setUser)
  }, [getUserInfo, userId])

  useEffect(() => {
    if (!isMemberGoalsLoading && memberGoals) {
      const allTasks = visibleTasks.reduce((acc, key) => {
        return acc.concat(memberGoals.action_plan[key])
      }, [] as IGoalTask[])
      setAllTasksList(allTasks)
      setIsLoading(false)
    }
  }, [memberGoals, isMemberGoalsLoading])

  return (
    <AppShell activePage='home' label='Goal Actions'>
      {user && (
        <>
          <Box alignItems='center' flexDirection='row' mb='08'>
            <Pressable alignItems='center' flexDirection='row' onPress={goBack}>
              <BackIcon fill='proAccent' />
            </Pressable>
          </Box>

          <PageHeader
            eyebrow='Clinical operations'
            title={`All goal's actions, ${user.first_name} ${user.last_name}`}
          />

          <DataTable<IGoalTask>
            columns={columns}
            data={allTasksList}
            emptyDescription='This member has no goal actions yet.'
            emptyTitle='Nothing to show'
            getRowKey={(row, rowIndex) =>
              row?.id?.toString() ?? String(rowIndex)
            }
            isLoading={isLoading}
          />
        </>
      )}
    </AppShell>
  )
}

export default GoalActions
